import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Countdown, { zeroPad } from "react-countdown";
import OtpInput from "react-otp-input";
import { toast } from "react-toastify";
import logo from "../../assets/images/logo-default.svg";
import { setLoading } from "../../core/feature/config/configSlice";
import { forgotPassword, mergeWithOtp } from "../../core/service/api_profile";

const maskContact = (value: string) => {
  if (!value) return "J***e@gmail.com";
  if (value.includes("@")) {
    const [name, domain] = value.split("@");
    if (name.length <= 2) return `${name[0]}***@${domain}`;
    return `${name[0]}***${name[name.length - 1]}@${domain}`;
  }
  if (value.length <= 4) return value;
  return `${value.slice(0, 2)}***${value.slice(-2)}`;
};

export const ConfirmationVerifyOTPDesktop = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location: any = useLocation();
  const dispatch = useDispatch();

  const [otp, setOtp] = useState("");
  const [data, setData] = useState<any>({});
  const [timer, setTimer] = useState(Date.now() + 300000);
  const [isExpired, setIsExpired] = useState(false);

  const handleOtpChange = (value: string) => {
    setOtp(value.replace(/\D/g, "").slice(0, 6));
  };

  const countdownRenderer = ({ minutes, seconds, completed }: any) => {
    if (completed) {
      return (
        <span className="font-montserrat text-[13px] font-bold text-psp-primary">
          00:00
        </span>
      );
    }
    return (
      <span className="font-montserrat text-[13px] font-bold text-psp-primary">
        {zeroPad(minutes)}:{zeroPad(seconds)}
      </span>
    );
  };

  const funCheckOTP = async () => {
    if (otp === "") return toast.warning(t("FORGOT-PASSWORD-OTP.enterCode"));

    const query: any = { newPassword: "", otp, phoneEmail: data.phoneEmail };
    mergeWithOtp(otp)
      .then(() => {
        navigate("/login", {
          state: { data: JSON.stringify(query) },
        });
      })
      .catch((err: any) => {
        console.log(err?.data?.message ?? err, "err");
        toast.error(t("FORGOT-PASSWORD-OTP.invalid"));
      });
  };

  const resendOtp = async () => {
    const query = {
      newPassword: "",
      otp: "",
      phoneEmail: data.phoneEmail,
      sendVia: data.sendVia,
      reqTime: data.reqTime,
    };
    setOtp("");
    setIsExpired(false);
    setTimer(Date.now() + 300000);
    dispatch(setLoading(true));
    forgotPassword(query)
      .then(() => {
        dispatch(setLoading(false));
      })
      .catch(() => {
        dispatch(setLoading(false));
      });
  };

  useEffect(() => {
    setTimer(Date.now() + 300000);
    setIsExpired(false);
    try {
      if (location?.state?.data) {
        setData(JSON.parse(location.state.data));
      }
    } catch {
      // abaikan jika state tidak ada (preview slicing)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen w-full bg-psp-primary flex items-center justify-center p-6 font-montserrat">
      <div className="w-full max-w-[440px] rounded-2xl bg-white shadow-lg px-8 pb-8 pt-8">
        {/* Logo */}
        <div className="flex justify-center">
          <img src={logo} alt="PSP" className="w-56 select-none" />
        </div>

        {/* Title */}
        <div className="mt-8 text-left">
          <h1 className="m-0 text-[20px] font-bold leading-6 text-psp-primary">
            Verifikasi Akun
          </h1>
          <p className="m-0 mt-2 text-[14px] font-normal leading-5 text-black">
            Masukan 6 Digit kode yang dikirim
          </p>
          <p className="m-0 mt-1 text-[15px] font-bold leading-5 text-black break-all">
            {maskContact(location?.state?.email)}
          </p>
        </div>

        {/* OTP boxes */}
        <div className="mt-6 flex justify-center">
          <OtpInput
            value={otp}
            onChange={handleOtpChange}
            numInputs={5}
            isInputNum
            shouldAutoFocus
            separator={<span style={{ width: "10px" }} />}
            containerStyle={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
            inputStyle={{
              width: "48px",
              height: "52px",
              borderRadius: "14px",
              border: "1px solid #CBD2DC",
              backgroundColor: "#FFFFFF",
              fontSize: "18px",
              fontWeight: 600,
              color: "#000",
              caretColor: "#0054E6",
              outline: "none",
            }}
            focusStyle={{
              border: "1.5px solid #0054E6",
              outline: "none",
            }}
          />
        </div>

        {/* Timer */}
        <div className="mt-6 flex items-center gap-1.5">
          <span className="text-[13px] font-normal text-black">
            Kode berlaku selama
          </span>
          <Countdown
            key={timer}
            date={timer}
            renderer={countdownRenderer}
            onComplete={() => setIsExpired(true)}
          />
        </div>

        {/* Warning */}
        <p className="m-0 mt-2 text-[13px] font-normal leading-[18px] text-black">
          Jangan perlihatkan kode OTP ini ke pihak yang tidak berkepentingan
        </p>

        {/* Button */}
        <button
          onClick={funCheckOTP}
          className="mt-8 h-[48px] w-full rounded-[12px] bg-psp-btn-primary text-[15px] font-bold text-white shadow-sm hover:bg-blue-800 active:bg-psp-active"
        >
          Selanjutnya
        </button>

        {/* Resend */}
        <div className="mt-5 text-center">
          <button
            onClick={resendOtp}
            className={`text-[14px] font-bold ${
              isExpired ? "text-psp-primary" : "text-psp-primary"
            } hover:underline`}
          >
            Kirim Ulang OTP
          </button>
        </div>
      </div>
    </div>
  );
};
